/* ==========================================================================
   IMBODY  ·  imbody-apple.js
   Confidential — IMBODY / CARBONSIX. Not for distribution.

   Two behaviours, both small and both scroll driven:

     1. The droplet band. A control bar that arrives with the section: it
        starts as a bubble, drops, then stretches into a full padded rail
        while the dots fade up inside it and the side control lands.

     2. The film lightbox. One overlay, reused by any section that carries
        a watch the film control.

   Everything writes custom properties and reads geometry inside a single
   requestAnimationFrame, so nothing here forces a layout mid scroll.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* One init per element, whatever re-runs us. */
  function once(el, key) {
    var flag = 'imbodyApple' + key;
    if (el.dataset[flag] === '1') return false;
    el.dataset[flag] = '1';
    return true;
  }

  function clamp01(n) { return n < 0 ? 0 : n > 1 ? 1 : n; }

  /* Settles rather than stops. */
  function easeOut(p) { return 1 - Math.pow(1 - p, 3); }

  /* ======================================================================
     1. The droplet band
     ====================================================================== */

  var dropped = [];
  var dropQueued = false;

  function readDrop(el) {
    var vh = window.innerHeight || 1;
    var r = el.getBoundingClientRect();

    /* Nowhere near the screen: leave the last written value alone. */
    if (r.bottom < -vh || r.top > vh * 2) return;

    /* Starts as the band crosses in from the bottom, finishes once it has
       climbed to the resting line. Both are shares of the viewport so the
       move reads the same on a phone and on a desk. */
    var from = vh * (parseFloat(el.dataset.dropFrom) || 0.99);
    var to = vh * (parseFloat(el.dataset.dropTo) || 0.7);
    var raw = clamp01((from - r.top) / Math.max(1, from - to));
    var p = easeOut(raw);

    var inner = clamp01((raw - 0.4) / 0.38);
    var side = clamp01((raw - 0.66) / 0.34);

    /* A coarser step than the eye can see. Rounding to three places
       meant a one pixel scroll nudged the value and forced another paint,
       which is what made the reveal look stepped. */
    p = Math.round(p * 200) / 200;
    inner = Math.round(inner * 200) / 200;
    side = Math.round(side * 200) / 200;

    if (el._d === p && el._di === inner && el._ds === side) return;
    el._d = p;
    el._di = inner;
    el._ds = side;

    el.style.setProperty('--imbody-drop', p);
    el.style.setProperty('--imbody-drop-in', inner);
    el.style.setProperty('--imbody-drop-side', side);

    /* Arrived (208). Lets a band drop whatever the move needed once it is
       at rest; the closer look pills use it to leave their row plain. */
    el.classList.toggle('imbody-drop--landed', p >= 1);
  }

  function dropFrame() {
    dropQueued = false;
    for (var i = 0; i < dropped.length; i++) readDrop(dropped[i]);
  }

  function queueDrop() {
    if (dropQueued) return;
    dropQueued = true;
    window.requestAnimationFrame(dropFrame);
  }

  var dropBound = false;

  function initDrop(root) {
    $$('[data-imbody-drop]', root).forEach(function (el) {
      if (!once(el, 'Drop')) return;
      el.classList.add('imbody-drop');

      if (REDUCED) {
        el.style.setProperty('--imbody-drop', 1);
        el.style.setProperty('--imbody-drop-in', 1);
        el.style.setProperty('--imbody-drop-side', 1);
        el.classList.add('imbody-drop--landed');
        return;
      }

      dropped.push(el);
      readDrop(el);
    });

    if (!dropBound && dropped.length) {
      dropBound = true;
      window.addEventListener('scroll', queueDrop, { passive: true });
      window.addEventListener('resize', queueDrop);
      window.addEventListener('orientationchange', queueDrop);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(queueDrop).catch(function () {});
      }
    }
    queueDrop();
  }

  /* ======================================================================
     2. The film lightbox
     ====================================================================== */

  var film = null;

  function buildFilm() {
    if (film) return film;
    film = document.createElement('div');
    film.className = 'imbody-film';
    film.hidden = true;
    film.innerHTML =
      '<div class="imbody-film__inner" data-film-inner>' +
      '<button type="button" class="imbody-film__close" data-film-close aria-label="Close">' +
      '<svg viewBox="0 0 16 16" fill="none" stroke-linecap="round"><path d="M3 3l10 10M13 3L3 13"/></svg>' +
      '</button>' +
      '</div>';
    document.body.appendChild(film);

    $('[data-film-close]', film).addEventListener('click', shutFilm);
    film.addEventListener('click', function (e) { if (e.target === film) shutFilm(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && film && !film.hidden) shutFilm();
    });
    return film;
  }

  function shutFilm() {
    if (!film) return;
    film.hidden = true;
    document.documentElement.style.overflow = '';
    var inner = $('[data-film-inner]', film);
    $$('video, iframe', inner).forEach(function (n) { n.remove(); });
  }

  function openFilm(src, kind) {
    var box = buildFilm();
    var inner = $('[data-film-inner]', box);
    $$('video, iframe', inner).forEach(function (n) { n.remove(); });

    var node;
    if (kind === 'embed') {
      node = document.createElement('iframe');
      node.src = src;
      node.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      node.setAttribute('allowfullscreen', '');
      node.setAttribute('title', 'Film');
    } else {
      node = document.createElement('video');
      node.src = src;
      node.controls = true;
      node.playsInline = true;
      node.setAttribute('playsinline', '');
      node.preload = 'metadata';
    }
    inner.appendChild(node);

    box.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    /* A node created a moment ago has loaded nothing, so play() rejects and
       the lightbox opens on a still frame. Same fix as everywhere else:
       treat the rejection as "not yet" and try again once it has data.

       Written out here rather than shared, because the helper in imbody.js
       lives in a different closure and this file has no load-order
       guarantee against it. An iframe has no play method, hence the guard. */
    if (node.play) {
      var p = node.play();
      if (p && p.catch) {
        p.catch(function () {
          var retry = function () {
            node.removeEventListener('loadeddata', retry);
            node.removeEventListener('canplay', retry);
            var q = node.play();
            if (q && q.catch) q.catch(function () {});
          };
          node.addEventListener('loadeddata', retry);
          node.addEventListener('canplay', retry);
        });
      }
    }
  }

  /* YouTube and Vimeo watch links become embeds so they play in place. */
  function toEmbed(url) {
    if (!url) return null;
    var m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
    if (m) return 'https://www.youtube.com/embed/' + m[1] + '?autoplay=1&rel=0&playsinline=1';
    m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (m) return 'https://player.vimeo.com/video/' + m[1] + '?autoplay=1';
    return null;
  }

  function initFilmLinks(root) {
    $$('[data-imbody-film]', root).forEach(function (btn) {
      if (!once(btn, 'Film')) return;
      btn.addEventListener('click', function (e) {
        var mp4 = btn.getAttribute('data-film-src');
        var link = btn.getAttribute('data-film-link');
        var embed = toEmbed(link);

        if (mp4) { e.preventDefault(); openFilm(mp4, 'file'); return; }
        if (embed) { e.preventDefault(); openFilm(embed, 'embed'); return; }
        /* No film and no embeddable link: an anchor follows its href. */
      });
    });
  }

  /* ======================================================================
     3. Closer look pills: give each one its place in the queue
     ====================================================================== */

  function initChipOrder(root) {
    $$('.imbody-closer-sec--v2 [data-imbody-chips]', root).forEach(function (rail) {
      if (!once(rail, 'Chips')) return;
      $$('[data-imbody-chip]', rail).forEach(function (chip, i) {
        chip.style.setProperty('--i', i);
      });
    });
  }


  /* ======================================================================
     In the box: the plus raises a sheet

     One overlay per section, one panel per piece. The rail stays where it
     was, so closing puts you back on the card you opened.
     ====================================================================== */

  function initKit(root) {
    $$('[data-imbody-kit]', root).forEach(function (sec) {
      if (!once(sec, 'Kit')) return;

      var sheet = $('[data-imbody-kit-sheet]', sec);
      if (!sheet) return;
      var panels = $$('[data-imbody-kit-panel]', sheet);
      var opener = null;

      function close() {
        sheet.hidden = true;
        panels.forEach(function (p) { p.hidden = true; });
        document.documentElement.style.overflow = '';
        if (opener && opener.focus) opener.focus();
        opener = null;
      }

      function open(i, btn) {
        if (!panels[i]) return;
        opener = btn || null;
        panels.forEach(function (p, k) { p.hidden = k !== i; });
        sheet.hidden = false;
        document.documentElement.style.overflow = 'hidden';
        var c = $('[data-imbody-kit-close]', panels[i]);
        if (c && c.focus) c.focus();
      }

      $$('[data-imbody-kit-open]', sec).forEach(function (btn) {
        btn.addEventListener('click', function () {
          open(parseInt(btn.getAttribute('data-imbody-kit-open'), 10) || 0, btn);
        });
      });

      $$('[data-imbody-kit-close]', sheet).forEach(function (b) {
        b.addEventListener('click', close);
      });

      /* A tap on the ground behind the card closes it. */
      sheet.addEventListener('click', function (e) { if (e.target === sheet) close(); });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !sheet.hidden) close();
      });
    });
  }


  /* ======================================================================
     Rotator: the brand bar raises a card
     ====================================================================== */

  function initBrand(root) {
    $$('[data-imbody-brand]', root).forEach(function (wrap) {
      if (!once(wrap, 'Brand')) return;
      var sheet = $('[data-imbody-brand-sheet]', wrap);
      var open = $('[data-imbody-brand-open]', wrap);
      if (!sheet || !open) return;
      var closeBtn = $('[data-imbody-brand-close]', sheet);
      var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      var IN_MS = reduced ? 0 : 560;
      var OUT_MS = reduced ? 0 : 340;

      /* Lifted out to the body, once, before it is ever shown.

         The sheet is position: fixed with inset: 0, which should make it
         full screen. It was not, because it lives inside the brand bar and
         that bar carries a backdrop-filter. A filter or backdrop-filter on
         an ancestor establishes a containing block for fixed descendants,
         exactly as a transform does, so inset: 0 was resolving against the
         bar rather than the viewport. That is the gap at the top.

         Moving it to the body puts it beyond every ancestor, so no property
         anyone adds to the section later can capture it again. Same lesson
         as the film overlay: keep overlays out of wrappers that are styled. */
      if (sheet.parentNode !== document.body) document.body.appendChild(sheet);

      /* Fetch and decode the page's pictures before anyone asks for them:
         as Learn more comes within reach of the screen, or the moment a
         finger or pointer lands on it. Decoded ahead, the hero is drawn in
         the first frame of the fade instead of arriving part way through. */
      var warmed = false;
      function warm() {
        if (warmed) return;
        warmed = true;
        $$('img', sheet).forEach(function (img) {
          if (img.getAttribute('loading') === 'lazy') img.loading = 'eager';
          if (img.decode) img.decode().catch(function () {});
        });
      }
      open.addEventListener('pointerenter', warm);
      open.addEventListener('touchstart', warm, { passive: true });
      open.addEventListener('focus', warm);
      /* Near is not the same as wanted. On arrival the button is already
         within reach, and fetching the page's pictures then put them in the
         way of the hero's. So a near button waits for the page to finish
         loading and a quiet moment; a finger or pointer on it still warms
         at once. */
      function afterLoad(fn) {
        var idle = function () {
          if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 3000 });
          else setTimeout(fn, 1200);
        };
        if (document.readyState === 'complete') idle();
        else window.addEventListener('load', idle, { once: true });
      }
      if ('IntersectionObserver' in window) {
        var near = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) { near.disconnect(); afterLoad(warm); }
          });
        }, { rootMargin: '600px 0px' });
        near.observe(open);
      }

      /* Hold the scroll position while the sheet is up.

         overflow: hidden on <html> does not stop iOS scrolling the page,
         and it does not preserve where you were. Fixing the body and
         offsetting it by the current scroll does both, and putting that
         scroll back on close means the page is exactly where you left it
         rather than at the top. */
      var lockedAt = 0;
      var state = 'closed';
      var timer = null;

      function lock() {
        lockedAt = window.pageYOffset || document.documentElement.scrollTop || 0;
        document.body.style.top = (-lockedAt) + 'px';
        document.documentElement.classList.add('imbody-sheet-open');
        document.body.classList.add('imbody-sheet-open');
      }
      function unlock() {
        document.documentElement.classList.remove('imbody-sheet-open');
        document.body.classList.remove('imbody-sheet-open');
        document.body.style.top = '';
        /* Put the page back instantly. html carries scroll-behavior: smooth,
           so a plain scrollTo animated the page from the top down to where it
           was, visibly. */
        var html = document.documentElement, was = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        window.scrollTo(0, lockedAt);
        html.style.scrollBehavior = was;
      }

      /* In: lock while the sheet is still invisible, then fade it up a frame
         later, so the layout the lock causes never lands inside the fade. A
         picture that has still not arrived waits unseen and fades in. */
      function show() {
        if (state === 'open' || state === 'opening') return;
        window.clearTimeout(timer);
        if (state === 'closing') {
          sheet.setAttribute('data-state', 'in');
          state = 'opening';
          timer = window.setTimeout(function () { state = 'open'; }, IN_MS);
          return;
        }
        warm();
        state = 'opening';
        sheet.removeAttribute('data-state');
        sheet.hidden = false;
        sheet.scrollTop = 0;
        lock();
        $$('.imbody-rotator__hero > img', sheet).forEach(function (img) {
          if (img.complete) return;
          img.setAttribute('data-pending', '');
          function done() { img.removeAttribute('data-pending'); }
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        });
        if (closeBtn && closeBtn.focus) {
          try { closeBtn.focus({ preventScroll: true }); } catch (err) { closeBtn.focus(); }
        }
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            if (state !== 'opening') return;
            sheet.setAttribute('data-state', 'in');
            timer = window.setTimeout(function () { state = 'open'; }, IN_MS);
          });
        });
      }

      /* Out: fade and settle, and only once it has gone unlock the page and
         put it back, so the page never moves while it can be seen. Focus
         goes back to Learn more without scrolling to it. */
      function shut() {
        if (state === 'closed' || state === 'closing') return;
        window.clearTimeout(timer);
        state = 'closing';
        sheet.setAttribute('data-state', 'out');
        timer = window.setTimeout(function () {
          sheet.hidden = true;
          sheet.removeAttribute('data-state');
          unlock();
          state = 'closed';
          if (open.focus) {
            try { open.focus({ preventScroll: true }); } catch (err) { open.focus(); }
          }
        }, OUT_MS);
      }

      open.addEventListener('click', show);
      if (closeBtn) closeBtn.addEventListener('click', shut);
      sheet.addEventListener('click', function (e) { if (e.target === sheet) shut(); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !sheet.hidden) shut();
      });
    });
  }


  /* ======================================================================
     Support: search and category pills

     Both narrow the same list. Typing searches across every category at
     once, since someone who knows the word does not know which drawer it
     lives in. A pill narrows to one and clears the search.
     ====================================================================== */

  function initSupport(root) {
    $$('[data-imbody-sup]', root).forEach(function (sec) {
      if (!once(sec, 'Sup')) return;

      var input = $('[data-imbody-sup-search]', sec);
      var pills = $$('[data-imbody-sup-filter] .imbody-sup__pill', sec);
      var items = $$('.imbody-sup__item', sec);
      var empty = $('[data-imbody-sup-empty]', sec);
      var catHead = $('[data-imbody-sup-cat]', sec);
      /* No catch-all pill any more: the first category is the default. */
      var first = pills[0];
      var cat = first ? (first.getAttribute('data-value') || '') : '';

      function apply() {
        var term = (input && input.value || '').trim().toLowerCase();
        var shown = 0;

        items.forEach(function (it) {
          var okCat = !cat || term || (it.getAttribute('data-cat') || '') === cat;
          var okTerm = !term || (it.getAttribute('data-text') || '').indexOf(term) !== -1;
          var show = okCat && okTerm;
          it.hidden = !show;
          if (!show) it.open = false;
          if (show) shown++;
        });

        if (empty) empty.hidden = shown !== 0;

        /* The heading names whatever is being shown, so the list is never
           an unlabelled wall of questions. */
        if (catHead) {
          var live = pills.filter(function (o) { return o.getAttribute('aria-pressed') === 'true'; })[0];
          catHead.textContent = term
            ? (catHead.getAttribute('data-search-label') || 'Results')
            : (live ? live.textContent.trim() : catHead.textContent);
        }
      }

      pills.forEach(function (p) {
        p.addEventListener('click', function () {
          cat = p.getAttribute('data-value') || '';
          pills.forEach(function (o) { o.setAttribute('aria-pressed', String(o === p)); });
          /* A pill is a fresh start, so a stale search term does not
             silently hide half of what was just asked for. */
          if (input) input.value = '';
          apply();
        });
      });

      if (input) {
        input.addEventListener('input', function () {
          /* Typing searches everything, so the pill goes back to all. */
          /* Typing looks everywhere, so no pill claims to be the filter. */
          if (input.value.trim()) {
            pills.forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
          } else if (first) {
            cat = first.getAttribute('data-value') || '';
            pills.forEach(function (o) { o.setAttribute('aria-pressed', String(o === first)); });
          }
          apply();
        });
      }

      apply();
    });
  }

  /* ====================================================================== */

  function init(root) {
    var scope = root || document;
    try { initDrop(scope); } catch (e) { if (window.console) console.error('[IMBODY] initDrop failed:', e); }
    try { initFilmLinks(scope); } catch (e) { if (window.console) console.error('[IMBODY] initFilmLinks failed:', e); }
    try { initChipOrder(scope); } catch (e) { if (window.console) console.error('[IMBODY] initChipOrder failed:', e); }
    try { initKit(scope); } catch (e) { if (window.console) console.error('[IMBODY] initKit failed:', e); }
    try { initBrand(scope); } catch (e) { if (window.console) console.error('[IMBODY] initBrand failed:', e); }
    try { initSupport(scope); } catch (e) { if (window.console) console.error('[IMBODY] initSupport failed:', e); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:select', function (e) { init(e.target); });

  window.IMBODY_APPLE = { init: init, openFilm: openFilm };
})();
